import { supabase } from '../lib/supabaseClient'
import { profilesService } from './database'
import type { Session } from '@supabase/supabase-js'
import type { Database } from '../lib/supabaseClient'

type Profile = Database['public']['Tables']['profiles']['Row']
type ProfileInsert = Database['public']['Tables']['profiles']['Insert']

export interface AuthUser {
  id: string
  email: string
  profile: Profile | null
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  fullName: string
  role?: 'admin' | 'professor' | 'aluno'
}

export interface AuthResponse {
  user: AuthUser | null
  error: string | null
}

// ============================================================================
// CACHE DE PERFIL
// ============================================================================

const PROFILE_CACHE_KEY = 'bizu_user_profile'

const cacheProfile = (userId: string, profile: Profile | null) => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ userId, profile, timestamp: Date.now() }))
  } catch (e) {
    console.error('Erro ao cachear perfil:', e)
  }
}

const getCachedProfile = (userId: string): Profile | null => {
  if (typeof window === 'undefined') return null
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY)
    if (!cached) return null

    const { userId: cachedUserId, profile, timestamp } = JSON.parse(cached)

    // Cache válido por 5 minutos
    if (cachedUserId === userId && Date.now() - timestamp < 5 * 60 * 1000) {
      return profile
    }
  } catch (e) {
    console.error('Erro ao ler cache:', e)
  }
  return null
}

const clearProfileCache = () => {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY)
  } catch (e) {
    console.error('Erro ao limpar cache:', e)
  }
}

// ============================================================================
// SERVIÇO DE AUTENTICAÇÃO
// ============================================================================

export const authService = {
  // Fazer login
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      })

      if (error) return { user: null, error: error.message }
      if (!data.user) return { user: null, error: 'Usuário não encontrado' }

      // Tentar cache primeiro
      let profile = getCachedProfile(data.user.id)

      // Se não houver cache, buscar do banco
      if (!profile) {
        profile = await profilesService.getById(data.user.id)
        cacheProfile(data.user.id, profile)
      }

      return {
        user: {
          id: data.user.id,
          email: data.user.email || '',
          profile
        },
        error: null
      }
    } catch (error) {
      console.error('Erro no login:', error)
      return { user: null, error: 'Erro interno do servidor' }
    }
  },

  // Fazer logout
  async logout(): Promise<{ error: string | null }> {
    try {
      clearProfileCache() // Limpar cache ao fazer logout
      const { error } = await supabase.auth.signOut()
      return { error: error?.message || null }
    } catch (error) {
      console.error('Erro no logout:', error)
      return { error: 'Erro interno do servidor' }
    }
  },

  // Registrar novo usuário
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      // Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            role: data.role || 'aluno'
          }
        }
      })

      if (authError) {
        return { user: null, error: authError.message }
      }

      if (!authData.user) {
        return { user: null, error: 'Erro ao criar usuário' }
      }

      // Criar perfil na tabela profiles
      const profileData: ProfileInsert = {
        id: authData.user.id,
        full_name: data.fullName,
        role: data.role || 'aluno',
        avatar_url: null
      }

      try {
        const profile = await profilesService.create(profileData)

        const authUser: AuthUser = {
          id: authData.user.id,
          email: authData.user.email || '',
          profile
        }

        return { user: authUser, error: null }
      } catch (profileError) {
        console.error('Erro ao criar perfil:', profileError)
        // Se falhar ao criar o perfil, ainda retorna o usuário
        const authUser: AuthUser = {
          id: authData.user.id,
          email: authData.user.email || '',
          profile: null
        }
        return { user: authUser, error: 'Usuário criado, mas erro ao criar perfil' }
      }
    } catch (error) {
      console.error('Erro no registro:', error)
      return { user: null, error: 'Erro interno do servidor' }
    }
  },

  // Obter usuário atual
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null

      // Tentar cache primeiro
      let profile = getCachedProfile(user.id)

      // Se não houver cache, buscar do banco
      if (!profile) {
        profile = await profilesService.getById(user.id)
        cacheProfile(user.id, profile)
      }

      return {
        id: user.id,
        email: user.email || '',
        profile
      }
    } catch (error) {
      console.error('Erro ao obter usuário atual:', error)
      return null
    }
  },

  // Obter sessão atual
  async getCurrentSession(): Promise<Session | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return session
    } catch (error) {
      console.error('Erro ao obter sessão:', error)
      return null
    }
  },

  // Resetar senha
  async resetPassword(email: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) {
        return { error: error.message }
      }

      return { error: null }
    } catch (error) {
      console.error('Erro ao resetar senha:', error)
      return { error: 'Erro interno do servidor' }
    }
  },

  // Atualizar senha
  async updatePassword(newPassword: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) {
        return { error: error.message }
      }

      return { error: null }
    } catch (error) {
      console.error('Erro ao atualizar senha:', error)
      return { error: 'Erro interno do servidor' }
    }
  },

  // Atualizar email
  async updateEmail(newEmail: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      })
      if (error) {
        return { error: error.message }
      }
      return { error: null }
    } catch (error) {
      console.error('Erro ao atualizar email:', error)
      return { error: 'Erro interno do servidor' }
    }
  },

  // Atualizar perfil do usuário
  async updateProfile(updates: Partial<Profile>): Promise<AuthResponse> {
    try {
      const user = await this.getCurrentUser()

      if (!user) {
        return { user: null, error: 'Usuário não autenticado' }
      }

      // Atualizar perfil na tabela profiles
      const updatedProfile = await profilesService.update(user.id, updates)

      const authUser: AuthUser = {
        ...user,
        profile: updatedProfile
      }

      return { user: authUser, error: null }
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      return { user: null, error: 'Erro ao atualizar perfil' }
    }
  },

  // Verificar se usuário é admin
  async isAdmin(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser()
      return user?.profile?.role === 'admin' || false
    } catch (error) {
      console.error('Erro ao verificar se é admin:', error)
      return false
    }
  },

  // Verificar se usuário é professor
  async isProfessor(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser()
      return user?.profile?.role === 'professor' || false
    } catch (error) {
      console.error('Erro ao verificar se é professor:', error)
      return false
    }
  },

  // Verificar se usuário é aluno
  async isAluno(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser()
      return user?.profile?.role === 'aluno' || false
    } catch (error) {
      console.error('Erro ao verificar se é aluno:', error)
      return false
    }
  },

  // Escutar mudanças de autenticação
  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        // Tentar cache primeiro
        let profile = getCachedProfile(session.user.id)

        // Se não houver cache, buscar do banco
        if (!profile) {
          profile = await profilesService.getById(session.user.id)
          cacheProfile(session.user.id, profile)
        }

        callback({
          id: session.user.id,
          email: session.user.email || '',
          profile
        })
      } else {
        clearProfileCache()
        callback(null)
      }
    })
  },

  // Verificar permissão específica
  checkPermission(userRole: string | undefined, permission: string): boolean {
    return checkPermission(userRole, permission)
  }
}

// ============================================================================
// HELPERS DE AUTENTICAÇÃO
// ============================================================================

// Verificar se email é válido
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Verificar se senha é forte
export const isStrongPassword = (password: string): boolean => {
  // Mínimo 8 caracteres, pelo menos 1 letra maiúscula, 1 minúscula e 1 número
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/
  return passwordRegex.test(password)
}

// Gerar mensagem de erro de senha
export const getPasswordRequirements = (): string => {
  return 'A senha deve ter pelo menos 8 caracteres, incluindo pelo menos 1 letra maiúscula, 1 minúscula e 1 número.'
}

// Verificar se usuário tem permissão para ação
export const hasPermission = (userRole: string | undefined, requiredRoles: string[]): boolean => {
  if (!userRole) return false
  return requiredRoles.includes(userRole)
}

// Constantes de roles
export const ROLES = {
  ADMIN: 'admin',
  PROFESSOR: 'professor',
  ALUNO: 'aluno'
} as const

// Permissões por role
export const PERMISSIONS = {
  // Admins podem fazer tudo
  [ROLES.ADMIN]: [
    'users.create', 'users.read', 'users.update', 'users.delete',
    'aulas.create', 'aulas.read', 'aulas.update', 'aulas.delete',
    'testes.create', 'testes.read', 'testes.update', 'testes.delete',
    'questoes.create', 'questoes.read', 'questoes.update', 'questoes.delete',
    'resultados.read', 'resultados.delete',
    'noticias.create', 'noticias.read', 'noticias.update', 'noticias.delete',
    'relatorios.read'
  ],

  // Professores podem gerenciar conteúdo educacional
  [ROLES.PROFESSOR]: [
    'aulas.create', 'aulas.read', 'aulas.update', 'aulas.delete',
    'testes.create', 'testes.read', 'testes.update', 'testes.delete',
    'questoes.create', 'questoes.read', 'questoes.update', 'questoes.delete',
    'resultados.read',
    'noticias.create', 'noticias.read', 'noticias.update',
    'relatorios.read'
  ],

  // Alunos podem apenas visualizar e fazer testes
  [ROLES.ALUNO]: [
    'aulas.read',
    'testes.read',
    'questoes.read',
    'resultados.read', // apenas seus próprios resultados
    'noticias.read'
  ]
} as const

// Verificar permissão específica
export const checkPermission = (userRole: string | undefined, permission: string): boolean => {
  if (!userRole) return false

  const rolePermissions = PERMISSIONS[userRole as keyof typeof PERMISSIONS]
  return rolePermissions ? (rolePermissions as readonly string[]).includes(permission) : false
}